import React from 'react'

// Full and mild hybrid icons are not available in @volkswagen-onehub/icons-core yet.
// They mirror its API and small (12px) variant, so they can be swapped 1:1 once released.

export interface FuelIconProps
    extends Pick<React.AriaAttributes, 'aria-label' | 'aria-hidden'> {
    variant?: 'small'
    title?: string
    className?: string
}

function SmallFuelIcon(
    props: FuelIconProps & {children: React.ReactNode}
): React.JSX.Element {
    const ariaHidden = props['aria-hidden']

    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="currentColor"
            className={props.className}
            aria-hidden={ariaHidden}
            aria-label={props['aria-label']}
            role={ariaHidden ? undefined : 'img'}
        >
            {props.title && <title>{props.title}</title>}
            {props.children}
        </svg>
    )
}

export function FullHybrid(props: FuelIconProps): React.JSX.Element {
    return (
        <SmallFuelIcon {...props}>
            <path d="M7.34018 5.43408L9.08456 6.00998L7.79483 9.74917C7.76831 9.84075 7.81664 9.93652 7.91073 9.97884C8.00482 10.0212 8.119 9.99848 8.18453 9.92445L11.896 5.39235C11.9941 5.27476 12.025 5.1225 11.9793 4.98133C11.9337 4.84015 11.8169 4.72651 11.664 4.67456L9.80829 4.05692L10.8939 0.242614C10.9162 0.14972 10.8629 0.0555171 10.7662 0.0171074C10.6696 -0.0213023 10.5562 0.0066548 10.4949 0.084032L7.08965 4.73298C6.99988 4.85199 6.976 5.00154 7.02493 5.13846C7.07385 5.27538 7.19013 5.38442 7.34018 5.43408Z" />
            <path
                d="M0.828308 8.75C0.328308 6.75 2.32831 4.75 3.82831 3.25C4.32825 3.75 6.82837 5.75 6.82837 8.25C6.82837 10.75 4.82837 11.25 3.82837 11.25C2.82837 11.25 1.32831 10.75 0.828308 8.75Z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.25"
                strokeLinejoin="round"
            />
            <path d="M3.35658 7.65137C3.2654 8.01606 3.32104 8.34557 3.44642 8.56055C3.56092 8.75665 3.73768 8.87494 4.00013 8.875V10.125C3.26279 10.1249 2.68924 9.74306 2.36635 9.18945C2.05457 8.65459 1.985 7.98367 2.14369 7.34863L3.35658 7.65137Z" />
        </SmallFuelIcon>
    )
}

export function MildHybrid(props: FuelIconProps): React.JSX.Element {
    return (
        <SmallFuelIcon {...props}>
            <path d="M6.00264 0.026001C6.65433 0.026931 7.03854 0.444683 7.26829 0.762329L7.73217 1.37268C7.71195 1.39472 7.6946 1.41562 7.68041 1.43225C7.60343 1.52249 7.53084 1.61138 7.41966 1.72229C7.29756 1.80567 7.16681 1.89969 7.04661 1.98792C7.04244 1.99065 7.03815 1.99391 7.03391 1.9967C6.96539 2.0403 6.88093 2.09021 6.76926 2.17053C6.7476 2.18614 6.72806 2.20407 6.70969 2.22229L6.196 1.54749C6.196 1.54749 6.08828 1.35925 6.00264 1.35608C5.91808 1.34613 5.83878 1.45985 5.79267 1.526L5.77607 1.54846L3.06604 5.14807C2.69099 5.6659 2.46889 6.27978 2.42442 6.9176C2.38005 7.55574 2.51509 8.19441 2.81506 8.7594C3.43227 9.92481 4.68898 10.6491 6.00264 10.6451C6.65427 10.6504 7.29937 10.4772 7.8601 10.1451C8.4204 9.81288 8.88093 9.33324 9.18924 8.7594C9.44811 8.27157 9.58377 7.72832 9.58769 7.17834C9.63816 7.19548 9.69158 7.20667 9.74785 7.20667H9.78398C9.82046 7.20679 9.89281 7.20714 10.0359 7.20764C10.2449 7.20837 10.5976 7.21227 10.9071 7.21643C10.898 7.96813 10.7153 8.70985 10.366 9.37952C9.51734 10.9819 7.82985 11.9784 6.00264 11.9742C5.10882 11.9802 4.22898 11.7404 3.46058 11.2838C2.6921 10.8268 2.06209 10.1678 1.64022 9.37952C1.22845 8.59071 1.04442 7.70261 1.10798 6.81506C1.17165 5.92756 1.48054 5.07516 2.00058 4.35315L4.6901 0.783813C4.93124 0.451956 5.35925 0.026001 6.00264 0.026001ZM5.07781 6.26917C4.95483 6.62314 5.00691 7.08272 5.24188 7.45569C5.46493 7.80617 5.8429 8.06695 6.39132 8.06702V9.39709C5.35485 9.39702 4.56788 8.8751 4.1188 8.16663C3.68285 7.47864 3.55595 6.59857 3.82094 5.83362L5.07781 6.26917Z" />
            <path d="M9.63359 0.614868C9.69409 0.641965 9.72743 0.708201 9.71367 0.773071L9.0281 3.45374L10.201 3.88733C10.2972 3.92397 10.3715 4.00423 10.4002 4.10315C10.4287 4.20195 10.409 4.30885 10.3475 4.39124L8.00171 7.57581C7.96045 7.62763 7.88819 7.64319 7.82885 7.61389C7.76942 7.58418 7.73891 7.51706 7.75561 7.45276L8.57008 4.82581L7.46752 4.42053C7.37299 4.3856 7.29922 4.30941 7.26829 4.2135C7.23739 4.11735 7.25264 4.01193 7.30931 3.92834L9.46171 0.661743C9.50055 0.607742 9.57269 0.587962 9.63359 0.614868Z" />
        </SmallFuelIcon>
    )
}
